import { Form, Pagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

export interface SampleListPaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SampleListPaginationBarProps {
  pagination: SampleListPaginationInfo;
  listLimit: number;
  onLimitChange: (limit: number) => void;
  onPageChange: (page: number | ((prev: number) => number)) => void;
  /** 表の上／下で同じバーを置くときの区別 */
  idPrefix?: string;
  /**
   * 1ページしかないときも件数表示・件数切替を出す（既定 true）。
   * 下バーなど二重表示を避けたいときは false。
   */
  showWhenSinglePage?: boolean;
  className?: string;
  /** 件数レンジ文言を隠す（stats 横に置くとき） */
  hideRange?: boolean;
  /** 件数セレクトを隠す（上バーと二重にしないとき） */
  hideLimit?: boolean;
}

/**
 * サンプル一覧の件数表示・件数切替・ページ送り（表の上下で再利用）。
 */
export default function SampleListPaginationBar({
  pagination,
  listLimit,
  onLimitChange,
  onPageChange,
  idPrefix = 'sample-list-paging',
  showWhenSinglePage = true,
  className = '',
  hideRange = false,
  hideLimit = false,
}: SampleListPaginationBarProps) {
  const { t } = useTranslation();

  if (pagination.total <= 0) return null;
  if (pagination.totalPages <= 1 && !showWhenSinglePage) return null;

  const multiPage = pagination.totalPages > 1;
  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.total);
  const showLimit = !hideLimit;
  const showControls = showLimit || multiPage;

  return (
    <div
      className={`d-flex flex-wrap justify-content-between align-items-center gap-2 ${className}`.trim()}
    >
      {!hideRange ? (
        <div className="text-muted small">
          {t('sample.list_range', {
            start,
            end,
            total: pagination.total,
          })}
        </div>
      ) : (
        <span className="visually-hidden">
          {t('sample.list_range', {
            start,
            end,
            total: pagination.total,
          })}
        </span>
      )}
      {showControls ? (
        <div className="d-flex align-items-center gap-2 ms-auto">
          {showLimit ? (
            <Form.Select
              id={`${idPrefix}-limit`}
              size="sm"
              className="sample-list-page-size"
              style={{ width: 'auto' }}
              value={listLimit}
              onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
              aria-label={t('sample.table_page_size')}
              title={t('datatables.length_title', {
                defaultValue: '1ページあたりの表示件数を変更',
              })}
            >
              {[25, 50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {t('sample.table_page_size_option', {
                    count: n,
                    defaultValue: String(n),
                  })}
                </option>
              ))}
            </Form.Select>
          ) : null}
          {multiPage ? (
            <Pagination className="mb-0 sample-list-pagination" size="sm">
              {/* Prev/Next は RB が visually-hidden 英語を固定するため Item で i18n */}
              <Pagination.Item
                className="d-none d-md-list-item"
                disabled={pagination.page <= 1}
                onClick={() => onPageChange(1)}
                aria-label={t('common.pagination.first', { defaultValue: 'First' })}
                title={t('common.pagination.first', { defaultValue: 'First' })}
              >
                «
              </Pagination.Item>
              <Pagination.Item
                disabled={pagination.page <= 1}
                onClick={() => onPageChange((p) => Math.max(1, p - 1))}
                aria-label={t('common.previous', { defaultValue: 'Previous' })}
                title={t('common.previous', { defaultValue: 'Previous' })}
              >
                {t('common.previous', { defaultValue: 'Previous' })}
              </Pagination.Item>
              <Pagination.Item active aria-current="page">
                {pagination.page}
              </Pagination.Item>
              <Pagination.Item
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => onPageChange((p) => Math.min(pagination.totalPages, p + 1))}
                aria-label={t('common.next', { defaultValue: 'Next' })}
                title={t('common.next', { defaultValue: 'Next' })}
              >
                {t('common.next', { defaultValue: 'Next' })}
              </Pagination.Item>
              <Pagination.Item
                className="d-none d-md-list-item"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => onPageChange(pagination.totalPages)}
                aria-label={t('common.pagination.last', { defaultValue: 'Last' })}
                title={t('common.pagination.last', { defaultValue: 'Last' })}
              >
                »
              </Pagination.Item>
            </Pagination>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
